import type { IProbeModelSlot } from "../types/probes";
import { ECoverage, EProbeAppliesTo, ERiskPriority } from "../types/probes";
import type { IProbeFormBaseValues } from "../utils/probes/types";

// NOTE: Maximum file size for probe uploads (10 MB)
export const MAX_PROBE_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// NOTE: Allowed MIME types for probe file uploads. Mirrors the server's
// `ALLOWED_PROBE_FILE_CONTENT_TYPES` exactly, because the presign DTO validates
// `contentType` against it with `@IsIn` — anything else comes back as
// "Unsupported file type". It doubles as the picker filter: the web
// `<input accept>` and mobile's `DocumentPicker.getDocumentAsync({ type })`.
// Keep the two lists identical; the picker's own variants live in
// `PROBE_FILE_MIME_ALIASES` below rather than being added here.
export const ALLOWED_PROBE_FILE_MIME_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
] as const;

// NOTE: Canonical extension list behind the dropzone hint ("PDF, DOCX, TXT and
// MD"). One entry per format — the MIME maps below carry the variants.
export const PROBE_FILE_SUPPORTED_EXTENSIONS = [
  "pdf",
  "docx",
  "txt",
  "md",
] as const;

// NOTE: Aliases a picker may report for a format the server knows under a
// different name. iOS `DocumentPicker` answers `text/x-markdown` for `.md` —
// the preferred MIME of Apple's `net.daringfireball.markdown` UTI — while the
// allow-list above only carries `text/markdown`, so an un-normalised value is
// rejected even though the file is exactly what the dropzone advertises
// (NSAI-5017). The `text/markdown` entry still admits `.md` at the picker, so
// the alias belongs here and not in the allow-list.
export const PROBE_FILE_MIME_ALIASES = {
  "text/x-markdown": "text/markdown",
} as const satisfies Record<
  string,
  (typeof ALLOWED_PROBE_FILE_MIME_TYPES)[number]
>;

// NOTE: Extension → MIME fallback for pickers that do not report a usable MIME
// type. Android document providers routinely hand back
// `application/octet-stream` (or nothing) for `.md`, which would otherwise fail
// the allow-list check even though the server accepts the file. The `satisfies`
// clause is the anti-drift guard: it fails the build if an extension above
// loses its mapping, or if a mapping points at a MIME type that is not allowed.
export const PROBE_FILE_EXTENSION_MIME_MAP = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  txt: "text/plain",
  md: "text/markdown",
} as const satisfies Record<
  (typeof PROBE_FILE_SUPPORTED_EXTENSIONS)[number],
  (typeof ALLOWED_PROBE_FILE_MIME_TYPES)[number]
>;

export const CHECKBOX_INDETERMINATE = "indeterminate" as const;

// NOTE: Default form values for new probe creation (shared between web and mobile)
export const PROBE_FORM_INITIAL_VALUES: IProbeFormBaseValues = {
  name: "",
  description: "",
  riskPriority: ERiskPriority.MEDIUM,
  coverage: ECoverage.MEDIUM,
  bannedBehaviors: [{ behavior: "" }],
  appliedModelUuids: [],
  appliedAssistantUuids: [],
  appliesTo: EProbeAppliesTo.INPUT,
  isEnabled: true,
};

// NOTE: Config-driven list so every probe/PII model slot shares one render path
// on both web and mobile instead of a hand-written block per slot.
export const PROBE_MODEL_SLOTS: readonly IProbeModelSlot[] = [
  {
    key: "probe_validation",
    testIdPrefix: "probe-validation",
    valueField: "probeValidationModelUuid",
    defaultField: "validationModel",
    shouldConfirmClear: true,
    isGuardModelAllowed: true,
  },
  {
    key: "probe_rerouting",
    testIdPrefix: "probe-rerouting",
    valueField: "probeReroutingModelUuid",
    defaultField: "reroutingModel",
  },
  {
    key: "probe_retrieval",
    testIdPrefix: "probe-retrieval",
    valueField: "probeRetrievalModelUuid",
    defaultField: "retrievalModel",
  },
  {
    key: "pii_intent_classifier",
    testIdPrefix: "pii-intent-classifier",
    valueField: "piiIntentClassifierModelUuid",
    defaultField: "piiIntentClassifierModel",
    isPiiIntentRoutingSlot: true,
  },
  {
    key: "pii_trusted",
    testIdPrefix: "pii-trusted",
    valueField: "piiTrustedModelUuid",
    defaultField: "piiTrustedModel",
    isPiiIntentRoutingSlot: true,
  },
] as const;

// NOTE: NSAI-5201 — reasoning-effort options for the probe validation slot.
// Mirrors the server's PROBE_REASONING_EFFORT_VALUES; "minimal" is deliberately
// absent because probe moderation has to reason over a numbered rule list and
// report which rule matched, and minimal effort degrades that attribution.
// `null` (rendered as the "default" option) means the probe call keeps using
// whatever effort the model itself implies.
export const PROBE_REASONING_EFFORT_OPTIONS = [
  "low",
  "medium",
  "high",
] as const;
